'use client';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';

import { useT } from '@/hooks';

interface MobileAmountInputProps {
  amount: string;
  onAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDecrease: () => void;
  onIncrease: () => void;
  onQuickAdd: (addAmount: number | 'max') => void;
}

export function MobileAmountInput({
  amount,
  onAmountChange,
  onDecrease,
  onIncrease,
  onQuickAdd,
}: MobileAmountInputProps) {
  const { t } = useT();
  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            size="small"
            onClick={onDecrease}
            sx={{
              bgcolor: 'action.hover',
              width: 40,
              height: 40,
              '&:hover': {
                bgcolor: 'action.selected',
              },
            }}
          >
            <RemoveIcon sx={{ fontSize: '1.25rem' }} />
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.primary',
              minWidth: '200px',
            }}
          >
            <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, mr: 1 }}>$</Typography>
            <TextField
              value={amount}
              onChange={onAmountChange}
              variant="standard"
              autoComplete="off"
              placeholder="0"
              inputProps={{
                inputMode: 'decimal',
                style: {
                  textAlign: 'left',
                  fontSize: '2.5rem',
                  fontWeight: 700,
                  padding: 0,
                  width: `${Math.max(40, amount.length * 22)}px`,
                  minWidth: '40px',
                },
              }}
              InputProps={{
                disableUnderline: true,
              }}
              sx={{
                width: 'auto',
              }}
            />
          </Box>
          <IconButton
            size="small"
            onClick={onIncrease}
            sx={{
              bgcolor: 'action.hover',
              width: 40,
              height: 40,
              '&:hover': {
                bgcolor: 'action.selected',
              },
            }}
          >
            <AddIcon sx={{ fontSize: '1.25rem' }} />
          </IconButton>
        </Box>
      </Box>
      {/* Quick Amount Buttons */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
        {[1, 20, 100].map((addAmount) => (
          <Button
            key={addAmount}
            variant="outlined"
            onClick={() => onQuickAdd(addAmount)}
            sx={{
              minWidth: 'auto',
              px: 2,
              py: 0.75,
              borderRadius: '6px',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 600,
              borderColor: 'divider',
              bgcolor: 'action.hover',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
                bgcolor: 'action.selected',
              },
            }}
          >
            +${addAmount}
          </Button>
        ))}
        <Button
          variant="outlined"
          onClick={() => onQuickAdd('max')}
          sx={{
            minWidth: 'auto',
            px: 2,
            py: 0.75,
            borderRadius: '6px',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            borderColor: 'divider',
            bgcolor: 'action.hover',
            color: 'text.secondary',
            '&:hover': {
              borderColor: 'primary.main',
              color: 'primary.main',
              bgcolor: 'action.selected',
            },
          }}
        >
          {t('trade.max')}
        </Button>
      </Box>
    </Box>
  );
}
